import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	formatDate,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
	sortDDLByLabel,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../services";
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { DateRange } from "@mui/icons-material";
import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { ReportsService } from "../../../services/ReportsService";
import { PayrollAdjustmentReportDataGridWithColumnToggled } from "./PayrollAdjustmentReportDataGridWithColumnToggled";
import { PayrollAdjustmentReportHeader } from "./PayrollAdjustmentReportHeader";
import { endOfDay } from "date-fns";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";
import { DepartmentsService } from "../../../services/DepartmentsService";
import { TeamsService } from "../../../services/TeamsService";
import { CRACreditService } from "../../../services/CRACreditService";

export interface IPayrollAdjustmentReportSetupPage { }
const PAYROLL_ADJUSTMENT_REPORT_QUERY_KEY = "payroll-adjustment-report";

const usePayrollAdjustmentReport = (
	runGetListAPICall: boolean,
	clientId: string,
	taxYear: number,
	employeeName: string,
	payPeriod: string,
	periodEndDate: string,
	team: string,
	department: string,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			PAYROLL_ADJUSTMENT_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			taxYear,
			employeeName,
			payPeriod,
			periodEndDate,
			team,
			department,
			startDate,
			endDate,
			params,
		],
		queryFn: () =>
			ReportsService.getPayrollAdjustmentReport(
				runGetListAPICall,
				clientId,
				taxYear,
				employeeName,
				payPeriod,
				periodEndDate,
				team,
				department,
				startDate,
				endDate,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetPayPeriodDDL = (
	clientId: string,
	employeePayPeriodId: string | null,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			"periods-ddl",
			employeePayPeriodId,
		],
		queryFn: () => {
			return CRACreditService.getPayPeriodDDL(
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["employee-status-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useDepartmentsDDL = (
	clientId: string,
) =>
	useQuery({
		queryKey: ["departments-list"],
		queryFn: (data) => {

			return DepartmentsService.getGetDepartmentsDDL(
				clientId,
			);
		},
		refetchOnWindowFocus: false,
	});


const useGetTeamsDDL = (clientId: string) =>
	useQuery({
		queryKey: ["teams-ddl", clientId],
		queryFn: () => {
			return TeamsService.getGetTeamsDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
// const useGetEndDateDDL = (clientId: string,) =>
// 	useQuery({
// 		queryKey: ["end-date-ddl"],
// 		queryFn: (data) => {
// 			return ReportsService.getEndDateDDL(clientId);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});


export const PayrollAdjustmentReport: React.FC<IPayrollAdjustmentReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [payrollAdjustmentReportData, setPayrollAdjustmentReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);
	const [selectedPeriodEndDate, setSelectedPeriodEndDate] = useState<string | null>(null);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const employeeName = formMethods.getValues("employeeName");
	const payPeriod = formMethods.getValues("payPeriod");
	const periodEndDate = formMethods.getValues("periodEndDate");
	const team = formMethods.getValues("team");
	const department = formMethods.getValues("department");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = usePayrollAdjustmentReport(
		runGetListAPICall,
		clientIdStore,
		taxYear,
		employeeName && employeeName,
		payPeriod && payPeriod,
		periodEndDate && periodEndDate,
		team && team,
		department && department,
		formatDate(startDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const payrollAdjustmentReportList: any | null = data;
	useEffect(() => {
		if (payrollAdjustmentReportList) {
			setPayrollAdjustmentReportData(payrollAdjustmentReportList);
		}
	}, [payrollAdjustmentReportList]);

	const { isLoading: isLoadingemployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const { isLoading: isLoadingdepartmentDDL, data: departmentDDL } = useDepartmentsDDL(clientId);
	const { isLoading: isLoadingTeamDDL, data: teamsDDL } = useGetTeamsDDL(clientId);
	const { isLoading: isLoadingPayrPeriodDDL, data: payPeriodDDL } = useGetPayFrequencyDDL(clientId, taxYear);
	// const { data: endDateDDL } = useGetEndDateDDL(clientId);
	const { isLoading: isLoadingPayPeriodEndDateDDL, data: payPeriodEndDateDDL } = useGetPayPeriodDDL(clientId, selectedPeriodEndDate, taxYear);

	const departmentsDDLItems = useMemo(() => {
		if (departmentDDL) {
			if (
				departmentDDL &&
				Array.isArray(departmentDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(departmentDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [departmentDDL]);

	const teamsDDLItems = useMemo(() => {
		if (teamsDDL) {
			if (
				teamsDDL &&
				Array.isArray(teamsDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(teamsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [teamsDDL]);

	const payPeriodDDLItems = useMemo(() => {
		if (payPeriodDDL) {
			if (
				payPeriodDDL &&
				Array.isArray(payPeriodDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodDDL]);

	//   after click apply button api calls with updated values and update payroll adjustment report data
	const PayrollAdjustmentReportDataAPICall = (
		employeeName: string,
		payPeriod: string,
		periodEndDate: string,
		team: string,
		department: string,
		startDate: string | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ReportsService.getPayrollAdjustmentReport(
					true,
					clientIdStore,
					taxYear,
					employeeName,
					payPeriod,
					periodEndDate,
					team,
					department,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setPayrollAdjustmentReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {

		PayrollAdjustmentReportDataAPICall(
			values.employeeName,
			values.payPeriod,
			values.periodEndDate,
			values.team,
			values.department,
			values.startDate,
			values.endDate,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getPayrollAdjustmentAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [PAYROLL_ADJUSTMENT_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedPayrollAdjustmentReportListItems = useMemo(() => {
		return (
			payrollAdjustmentReportData &&
			payrollAdjustmentReportData.items &&
			payrollAdjustmentReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [payrollAdjustmentReportData]);

	const onEmployeeStatusSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		formMethods.setValue('employee', "all")
		setEmployeeStatus(event.value);
		// setSelectedEmployee(null);
		// formInitialState();
		formMethods.setValue('employeeStatus', event.value)
	};


	// // getting latest updated values of form fields using watch method
	// const { watch } = formMethods;
	// const values = watch([
	// 	"employee",
	// 	"employeeStatus",
	// 	"startDate",
	// 	"endDate",
	// 	"dateRange",
	// ]);

	// const savePdfAsFile = (xmlData: any, filename: any) => {
	// 	const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
	// 	FileSaver.saveAs(blob, filename);
	// };

	// const onExportButtonClick = async (selectedOption: string) => {
	// 	const [employee, employeeStatus, startDate, endDate, dateRange] = values;

	// 	try {
	// 		setIsLoading(true);
	// 		const response = await ReportsService.exportSalaryProfileReport(
	// 			selectedOption,
	// 			clientIdStore,
	// 			employeeStatus,
	// 			employee,
	// 			formatDate(startDate),
	// 			formatDate(endDate),
	// 			dateRange,
	// 			queryParams
	// 		);

	// 		if (response) {
	// 			if (selectedOption === "PDF") {
	// 				if (response.status === 400) {
	// 					setSnackbarData({
	// 						open: true,
	// 						message: response.data.message
	// 							? response.data.message
	// 							: "Something went wrong.",

	// 						severity: "error",
	// 					});
	// 				} else {
	// 					const filename = `${clientName} - Payroll-Adjustment-Report.pdf`;
	// 					savePdfAsFile(response, filename);
	// 				}
	// 			} else if (selectedOption === "XLS") {
	// 				if (response.status === 400) {
	// 					setSnackbarData({
	// 						open: true,
	// 						message: response.data.message
	// 							? response.data.message
	// 							: "Something went wrong.",

	// 						severity: "error",
	// 					});
	// 				} else {
	// 					const filename = `${clientName} - Payroll-Adjustment-Report.xlsx`;
	// 					savePdfAsFile(response, filename);
	// 				}
	// 			} else {
	// 				const fileType =
	// 					selectedOption === "XLS"
	// 						? "application/vnd.ms-excel"
	// 						: "text/csv;charset=utf-8;";
	// 				const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

	// 				const blob = new Blob([response], { type: fileType });
	// 				FileSaver.saveAs(blob, `${clientName} - Payroll-Adjustment-Report.${fileExtension}`);
	// 			}
	// 		} else {
	// 			console.error("Error: No data received from the server");
	// 		}
	// 	} catch (err) {

	// 		setIsLoading(false);
	// 	} finally {
	// 		setIsLoading(false);
	// 	}

	// };

	// const expandedLabels = ["Reports", "Payroll"];
	// const expandedPayrollAdjustmentReportListOrSetupItem = "Payroll Adjustment";
	// const enhanceData = (data: any[], parentLabel?: string): any[] => {
	// 	return data.map((item) => {
	// 		let isExpanded = expandedLabels.includes(item.label);
	// 		if (
	// 			parentLabel === "Payroll" &&
	// 			item.label === expandedPayrollAdjustmentReportListOrSetupItem
	// 		) {
	// 			isExpanded = true;
	// 		}
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: item.items ? isExpanded : false,
	// 			items: item.items ? enhanceData(item.items, item.label) : undefined,
	// 			path: parentLabel === "Payroll" && item.label === expandedPayrollAdjustmentReportListOrSetupItem && "/reports/payroll-adjustment",
	// 		};
	// 		return enhancedItem;
	// 	});
	// };

	const expandedLabels = ["Reports"]; // ❌ Remove "Payroll" from here

	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];

			// "Payroll" should only auto-expand when it's nested under "Reports"
			const isPayrollUnderReports =
				item.label === "Payroll" && ancestorChain.includes("Reports");

			// "Payroll Adjustment" should only expand when under "Reports > Payroll"
			const isPayrollAdjustmentUnderReportsPayroll =
				item.label === "Payroll Adjustment" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Payroll");

			const isExpanded =
				expandedLabels.includes(item.label) || // e.g. "Reports"
				isPayrollUnderReports ||
				isPayrollAdjustmentUnderReportsPayroll;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: isPayrollAdjustmentUnderReportsPayroll
					? "/reports/payroll-adjustment"
					: item.path ?? undefined,
			};
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>

			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Payroll Adjustment Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<PayrollAdjustmentReportHeader
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						employeeDDLItems={employeeDDL && sortDDLByLabel(employeeDDL)}
						departmentsDDL={departmentsDDLItems}
						teamsDDL={teamsDDLItems}
						payPeriodDDLItems={payPeriodDDLItems}
						onPeriodEndDateSelectionChangeHandler={(value: string) => setSelectedPeriodEndDate(value)}
						payPeriodEndDateDDL={payPeriodEndDateDDL ?? []}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading || isLoadingPayrPeriodDDL || isLoadingTeamDDL || isLoadingdepartmentDDL || isLoadingemployeeDDL || isLoadingPayPeriodEndDateDDL
					}
					testId="payroll-adjustment-report"
				/>

				<PayrollAdjustmentReportDataGridWithColumnToggled
					columns={
						payrollAdjustmentReportData?.headerColumns
							? payrollAdjustmentReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// payrollAdjustmentReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						payrollAdjustmentReportData
							&& payrollAdjustmentReportData
								?.totalHeaderColumns &&
							Object.keys(payrollAdjustmentReportData.totalHeaderColumns).length !== 0
							? payrollAdjustmentReportData.totalHeaderColumns
							: {}
						// payrollAdjustmentReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						payrollAdjustmentReportData && updatedPayrollAdjustmentReportListItems
							? updatedPayrollAdjustmentReportListItems
							: []
						// payrollAdjustmentReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						payrollAdjustmentReportData && payrollAdjustmentReportData.total
							? payrollAdjustmentReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Payroll Adjustment Report"}
				// onExportButtonClick={onExportButtonClick}
				/>

				{payrollAdjustmentReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
